import { useEffect, useRef, useState } from "react";
import { css, cx } from "styled-system/css";
import { Check, ChevronDown } from "lucide-react";

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
};

/** Dropdown custom del sistema — reemplaza al <select> nativo del navegador (sin chrome del SO, estilable). */
export function Select<T extends string = string>({
  id,
  value,
  onChange,
  options,
  placeholder = "Elegí una opción…",
  disabled = false,
  className,
}: {
  id?: string;
  value: T | "";
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={rootRef} className={cx(css({ position: "relative" }), className)}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "2",
          width: "100%",
          px: "3",
          py: "2",
          rounded: "md",
          borderWidth: "1px",
          borderColor: "border.DEFAULT",
          bg: "bg.DEFAULT",
          fontSize: "sm",
          textAlign: "left",
          cursor: "pointer",
          opacity: disabled ? 0.6 : 1,
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          _disabled: { cursor: "not-allowed" },
          _focusVisible: { outline: "none", borderColor: "accent.DEFAULT", boxShadow: "0 0 0 1px token(colors.brand.500)" },
        })}
      >
        <span className={css({ truncate: true, color: selected ? "fg.DEFAULT" : "fg.muted" })}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          aria-hidden
          className={css({ flexShrink: 0, color: "fg.muted", transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)" })}
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className={css({
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 50,
            bg: "white",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            rounded: "lg",
            shadow: "md",
            overflow: "hidden",
            maxH: "56",
            overflowY: "auto",
            py: "1",
          })}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li key={opt.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "2",
                    w: "100%",
                    px: "3",
                    py: "2",
                    fontSize: "sm",
                    textAlign: "left",
                    bg: isSelected ? "#eef2ff" : "transparent",
                    color: isSelected ? "#4338ca" : "#0f172a",
                    border: "none",
                    cursor: "pointer",
                    _hover: { bg: isSelected ? "#e0e7ff" : "#f1f5f9" },
                  })}
                >
                  {opt.label}
                  {isSelected && <Check size={14} aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
